import React, { useCallback, useState, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  rectSortingStrategy, arrayMove, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Box,
  Chip,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  LinearProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, PictureAsPdf as PictureAsPdfIcon, Visibility as VisibilityIcon, CameraAlt as CameraAltIcon, OpenInNew as OpenInNewIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { Project } from '../../types/Project';
import { useOneDriveAuth } from '../../contexts/OneDriveAuthContext';
import { isCorporateOneDriveConfigured } from '../../config/onedriveConfig';
import { resolveCorporateDriveId, uploadFileToFolderById, ensureExecutionFolder, getOrCreateChildFolderById, fetchDriveItemBlob } from '../../services/onedriveFolderService';
import dataService from '../../services/dataService';
import {
  type ReportCompanyKey,
  WBSItem,
  loadWBS,
  saveWBS,
  parseWBSNum,
  ProgressSnapshot,
  StoredProgressReport,
  getProgressReports,
  saveProgressReport,
  updateProgressReport,
  deleteProgressReport,
  migrateProgressSnapshotsFromLocalStorage,
} from '../ProjectDetails';
import { arialNarrowBase64 } from '../../fonts/arialNarrowBase64';
import { parseClientApprover } from '../../utils/clientApprover';
import { buildProgressReportPdf, type ProgressReportPhoto, type ProgressReportRow } from '../../utils/reports/progressReportPdf';
import { appendPdfs, fetchScheduleTasks, renderScheduleAttachment } from '../../utils/reports/scheduleAnnex';
import { saveAs } from 'file-saver';
import { REPORT_ISSUERS } from '../../utils/reports/reportPdfKit';
import { loadReportLogo } from '../../utils/reports/reportLogo';

const NET_PACIFIC_COLORS = { primary: '#2c5aa0', secondary: '#1e4a72' };

interface PhotoItem {
  localId: string;
  file?: File;
  previewUrl?: string;        // object URL for local preview (newly added only)
  caption?: string;           // user-entered caption shown under the photo in the PDF
  // populated after successful OneDrive upload
  oneDriveId?: string;
  filename?: string;
  webUrl?: string;
  uploadedAt?: string;
  thumbnailDataUrl?: string;  // ~120px JPEG stored at upload time; survives page reload
  uploadStatus: 'uploading' | 'done' | 'error';
  errorMessage?: string;
}

/**
 * Generic render-prop wrapper that makes an element sortable via @dnd-kit.
 * The drag listeners are applied to a handle (not the whole element), and the
 * sortable node ref + transform style go on the host element (a TableRow or a Box).
 */
function SortableItem({ id, children }: {
  id: string;
  children: (args: Pick<ReturnType<typeof useSortable>, 'setNodeRef' | 'attributes' | 'listeners'> & { style: CSSProperties }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 1 : undefined,
  };
  return <>{children({ setNodeRef, attributes, listeners, style })}</>;
}

/**
 * Generate a small thumbnail (~120px longest edge, JPEG 0.65) from a photo blob.
 * Also corrects EXIF orientation so the on-screen thumbnail is upright.
 */
async function generateThumbnail(source: Blob): Promise<string> {
  const MAX = 120;
  try {
    const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
    const scale = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    return canvas.toDataURL('image/jpeg', 0.65);
  } catch {
    return '';
  }
}

/**
 * Convert a HEIC/HEIF file to JPEG. Returns the original file unchanged for
 * any other format. The returned File has a .jpg extension so browsers can
 * render it with createObjectURL / createImageBitmap.
 */
async function convertHeicToJpeg(file: File): Promise<File> {
  const name = file.name.toLowerCase();
  const isHeic = name.endsWith('.heic') || name.endsWith('.heif') ||
    file.type === 'image/heic' || file.type === 'image/heif';
  if (!isHeic) return file;
  try {
    const heic2any = (await import('heic2any')).default;
    const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(result) ? result[0] : result;
    const jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], jpegName, { type: 'image/jpeg' });
  } catch {
    return file; // fall back to original if conversion fails
  }
}

/**
 * Bake EXIF orientation into a canvas so jsPDF sees an already-rotated image.
 * Falls back to a plain FileReader data URL if createImageBitmap is unavailable.
 */
async function normalizeImageOrientation(source: Blob): Promise<string> {
  try {
    const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    return canvas.toDataURL('image/jpeg', 0.9);
  } catch {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(source);
    });
  }
}

const wbsInputSx = {
  '& .MuiOutlinedInput-root': { fontSize: '0.9375rem', backgroundColor: '#fff', '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#cbd5e1' }, '&.Mui-focused fieldset': { borderWidth: '1px', borderColor: NET_PACIFIC_COLORS.primary } },
  '& .MuiInputBase-input': { py: 1, px: 1.25, fontSize: '0.9375rem', color: '#1e293b' },
};
const wbsNumInputSx = {
  ...wbsInputSx,
  '& .MuiInputBase-input': { ...wbsInputSx['& .MuiInputBase-input'], textAlign: 'right', fontWeight: 600 },
  '& .MuiInputBase-input[type=number]': { MozAppearance: 'textfield' },
};

const WBS_TEMPLATES: Record<string, { label: string; items: { code: string; name: string }[] }> = {
  automation: {
    label: 'Automation Project',
    items: [
      { code: '1', name: 'Engineering Services' },
      { code: '1.1', name: 'Design Documents' },
      { code: '1.2', name: 'PLC Programming' },
      { code: '1.3', name: 'Factory Acceptance Test' },
      { code: '2', name: 'Installation' },
      { code: '2.1', name: 'Panel Fabrication' },
      { code: '2.2', name: 'Site Installation' },
      { code: '2.3', name: 'Site Acceptance Test' },
      { code: '3', name: 'Commissioning' },
      { code: '3.1', name: 'System Testing' },
      { code: '3.2', name: 'Handover' },
    ],
  },
  construction: {
    label: 'Construction Project',
    items: [
      { code: '1', name: 'Mobilization' },
      { code: '1.1', name: 'Site Survey' },
      { code: '1.2', name: 'Equipment Deployment' },
      { code: '2', name: 'Civil Works' },
      { code: '2.1', name: 'Structural' },
      { code: '2.2', name: 'Finishing' },
      { code: '3', name: 'Mechanical' },
      { code: '3.1', name: 'Piping' },
      { code: '3.2', name: 'Equipment Installation' },
      { code: '4', name: 'Electrical' },
      { code: '4.1', name: 'Roughing-in' },
      { code: '4.2', name: 'Termination & Testing' },
      { code: '5', name: 'Testing & Commissioning' },
      { code: '5.1', name: 'Pre-commissioning' },
      { code: '5.2', name: 'Commissioning & Handover' },
    ],
  },
  service: {
    label: 'Service Contract',
    items: [
      { code: '1', name: 'Assessment' },
      { code: '1.1', name: 'Site Inspection' },
      { code: '1.2', name: 'Report & Recommendations' },
      { code: '2', name: 'Execution' },
      { code: '2.1', name: 'Service Delivery' },
      { code: '2.2', name: 'Verification' },
      { code: '3', name: 'Documentation & Handover' },
      { code: '3.1', name: 'As-built Documentation' },
      { code: '3.2', name: 'Client Acceptance' },
    ],
  },
};

export interface Approver {
  name: string;
  designation: string;
  company: string;
}

export interface ProgressReportTabProps {
  project: Project;
  currentUser: { full_name?: string | null; username?: string; email?: string } | null;
  reportCompany: ReportCompanyKey;
  setReportCompany: (v: ReportCompanyKey) => void;
  preparedBy: { name: string; designation: string; company: string; date: string };
  setPreparedBy: React.Dispatch<React.SetStateAction<{ name: string; designation: string; company: string; date: string }>>;
  onPreview: (blob: Blob, title: string) => void;
  /** Pre-fill the PB # field (passed from ProjectDetails milestone row link) */
  initialPb?: string;
  /** Resolved client contact to use as the "Approved by" signatory */
  clientApprover?: { name: string; designation: string; company: string };
  /** All contacts from the project's client — powers the approver name autocomplete */
  clientContacts?: { name: string; designation: string; company: string }[];
}

/** Accordion sections styled as the design guide's content papers (DESIGN_PHILOSOPHY §8). */
const sectionSx = {
  mb: 1.5,
  borderRadius: '8px !important',
  overflow: 'hidden',
  border: '1px solid #e2e8f0',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  '&:before': { display: 'none' },
} as const;
const summarySx = { minHeight: 52, px: 1.5, '&.Mui-expanded': { borderBottom: '1px solid #e0e0e0' }, '& .MuiAccordionSummary-content': { alignItems: 'center', my: 1 } } as const;
const sectionTitleSx = { fontSize: '1.1rem', fontWeight: 600, color: NET_PACIFIC_COLORS.primary, flex: 1 } as const;
const wbsHeadSx = { fontWeight: 600, fontSize: '0.875rem', bgcolor: '#f8fafc', color: 'text.primary', borderBottom: '2px solid #e2e8f0', py: 1.25, px: 1.5, whiteSpace: 'nowrap' } as const;
const wbsTotalSx = { fontWeight: 700, fontSize: '0.875rem', color: 'text.primary', borderTop: '2px solid #e2e8f0', borderBottom: 0, bgcolor: '#f8fafc', py: 1.25, px: 1.5 } as const;
const savedHeadSx = { fontWeight: 600, fontSize: '0.875rem', bgcolor: '#f8fafc' } as const;

const ProgressReportTab: React.FC<ProgressReportTabProps> = ({
  project,
  currentUser,
  reportCompany,
  setReportCompany: _setReportCompany,
  preparedBy,
  setPreparedBy,
  onPreview,
  initialPb,
  clientApprover,
  clientContacts = [],
}) => {
  const navigate = useNavigate();
  const {
    isConfigured: oneDriveConfigured,
    isAuthenticated: oneDriveSignedIn,
    isLoading: oneDriveAuthLoading,
    login: oneDriveLogin,
    getAccessToken: getOneDriveToken,
  } = useOneDriveAuth();
  // Local copy of execution folder id — updated if we auto-create the folder on first export
  const [localExecutionFolderId, setLocalExecutionFolderId] = useState(project.executionFolderId);
  useEffect(() => { setLocalExecutionFolderId(project.executionFolderId); }, [project.id, project.executionFolderId]);
  // Resolved via project_no — ensures PDFs land in the IOCT ops folder, not a PCS subfolder
  const [resolvedExecFolderId, setResolvedExecFolderId] = useState<string | null>(null);
  useEffect(() => { setResolvedExecFolderId(null); }, [project.id]);

  // Photo attachment state (general site photos for the progress report)
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [photosFolderId, setPhotosFolderId] = useState<string | null>(null);
  const [photosFolderUrl, setPhotosFolderUrl] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  // Revoke object URLs on unmount to avoid memory leaks
  const photosRef = useRef<PhotoItem[]>([]);
  useEffect(() => { photosRef.current = photos; }, [photos]);
  useEffect(() => () => {
    photosRef.current.forEach(p => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); });
  }, []);
  // Reset photos + folder refs when switching projects
  useEffect(() => {
    setPhotos(prev => {
      prev.forEach(p => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); });
      return [];
    });
    setPhotosFolderId(null);
    setPhotosFolderUrl(null);
  }, [project.id]);

  const [wbsItems, setWbsItems] = useState<WBSItem[]>([]);

  // Work Schedule annexes (Gantt chart / S-Curve) — remembered per project.
  const scheduleAttachKey = `progressReportScheduleAttach-${project.id}`;
  const [scheduleAttach, setScheduleAttach] = useState<{ gantt: boolean; sCurve: boolean }>({ gantt: false, sCurve: false });
  const [scheduleTaskCount, setScheduleTaskCount] = useState<number | null>(null);
  useEffect(() => {
    try {
      const v = JSON.parse(localStorage.getItem(scheduleAttachKey) || 'null');
      setScheduleAttach({ gantt: !!v?.gantt, sCurve: !!v?.sCurve });
    } catch {
      setScheduleAttach({ gantt: false, sCurve: false });
    }
    setScheduleTaskCount(null);
    let cancelled = false;
    fetchScheduleTasks(String(project.id))
      .then((tasks) => { if (!cancelled) setScheduleTaskCount(tasks.length); })
      .catch(() => { if (!cancelled) setScheduleTaskCount(0); });
    return () => { cancelled = true; };
  }, [project.id, scheduleAttachKey]);
  const toggleScheduleAttach = (key: 'gantt' | 'sCurve') => setScheduleAttach((prev) => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem(scheduleAttachKey, JSON.stringify(next)); } catch { /* per-device convenience only */ }
    return next;
  });
  const hasSchedule = (scheduleTaskCount ?? 0) > 0;
  const [pbInput, setPbInput] = useState(initialPb ?? '');
  const [showBillingHint, setShowBillingHint] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFeedback, setExportFeedback] = useState<{ severity: 'success' | 'warning' | 'error'; message: string } | null>(null);
  const [saveFeedback, setSaveFeedback] = useState(false);
  const [savedReports, setSavedReports] = useState<StoredProgressReport[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [wbsDraft, setWbsDraft] = useState<Record<string, { progress?: string; weight?: string }>>({});
  const [approvers, setApprovers] = useState<Approver[]>(() => {
    // Prefer client DB contact; fall back to parsing the legacy text field
    if (clientApprover?.name || clientApprover?.designation) {
      return [{ name: clientApprover.name, designation: clientApprover.designation, company: clientApprover.company }];
    }
    const { name: approverName, designation: approverDesignation } = parseClientApprover(project.client_approver);
    const approverCompany = (project.account_name || '').trim();
    if (approverName || approverDesignation || approverCompany) {
      return [{ name: approverName, designation: approverDesignation, company: approverCompany }];
    }
    return [];
  });

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    setup: true,
    wbs: true,
    signatories: false,
    photos: false,
    savedReports: false,
  });
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const [editingPreparedBy, setEditingPreparedBy] = useState(false);
  const [editingApproverIndex, setEditingApproverIndex] = useState<number | null>(null);
  const [addApproverInput, setAddApproverInput] = useState('');

  useEffect(() => {
    setWbsItems(loadWBS(project.id));
  }, [project.id]);

  // Sync initialPb into the PB# field whenever it changes (e.g. user arrives via milestone row link)
  useEffect(() => {
    if (initialPb) setPbInput(initialPb);
  }, [initialPb]);

  // Update the approvers list when the resolved client contact arrives (async fetch in ReportsPage).
  // Only update slot 0 — preserve any extra approvers the user has manually added.
  useEffect(() => {
    if (clientApprover?.name || clientApprover?.designation) {
      const first = { name: clientApprover.name, designation: clientApprover.designation, company: clientApprover.company };
      setApprovers(prev => prev.length === 0 ? [first] : [first, ...prev.slice(1)]);
    }
  }, [clientApprover]);

  // Load saved progress reports from Firestore (and one-time migrate any localStorage snapshots)
  const loadSavedReports = useCallback(async () => {
    setSavedLoading(true);
    try {
      const reports = await getProgressReports(project.id);
      setSavedReports(reports);
    } catch {
      // Keep stale list on transient error — don't wipe what the user can see.
    } finally {
      setSavedLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    setEditingReportId(null);
    let cancelled = false;
    (async () => {
      await migrateProgressSnapshotsFromLocalStorage().catch(() => {});
      if (!cancelled) await loadSavedReports();
    })();
    return () => { cancelled = true; };
  }, [project.id, loadSavedReports]);

  // Auto-suggest next PB# based on saved reports
  useEffect(() => {
    if (pbInput || initialPb) return; // don't override user input or initialPb
    if (savedReports.length === 0) return;
    const maxPb = savedReports.reduce((max, s) => {
      const num = parseInt(s.pbNumber, 10);
      return !isNaN(num) && num > max ? num : max;
    }, 0);
    if (maxPb > 0) {
      setPbInput(String(maxPb + 1));
    }
  }, [savedReports, initialPb]); // eslint-disable-line react-hooks/exhaustive-deps -- pbInput excluded to avoid re-triggering on user input

  // Helper function to check if a code is a parent (has children)
  const isParentItem = useCallback((code: string, allItems: WBSItem[]): boolean => {
    if (!code || code.trim() === '') return false;
    const parentCode = code.trim();
    return allItems.some((item) => {
      const itemCode = (item.code || '').trim();
      return itemCode.startsWith(parentCode + '.') && itemCode !== parentCode;
    });
  }, []);

  // Helper function to get child items of a parent
  const getChildItems = useCallback((parentCode: string, allItems: WBSItem[]): WBSItem[] => {
    const code = (parentCode || '').trim();
    if (!code) return [];
    return allItems.filter((item) => {
      const itemCode = (item.code || '').trim();
      return itemCode.startsWith(code + '.') && itemCode !== code;
    });
  }, []);

  // Helper function to calculate totals for a parent item
  const calculateParentTotals = useCallback((parentCode: string, allItems: WBSItem[]): { weight: number; progress: number } => {
    const children = getChildItems(parentCode, allItems);
    if (children.length === 0) return { weight: 0, progress: 0 };
    
    const totalWeight = children.reduce((sum, item) => sum + parseWBSNum(item.weight), 0);
    const weightedSum = children.reduce((sum, item) => {
      const weight = parseWBSNum(item.weight);
      const progress = parseWBSNum(item.progress);
      return sum + (weight * progress) / 100;
    }, 0);
    
    const progress = totalWeight > 0 ? (weightedSum / totalWeight) * 100 : 0;
    return { weight: totalWeight, progress };
  }, [getChildItems]);

  // Helper function to get indentation level (0 = parent, 1 = child, 2 = grandchild, etc.)
  const getIndentLevel = (code: string): number => {
    if (!code || code.trim() === '') return 0;
    const matches = code.match(/\./g);
    return matches ? matches.length : 0;
  };

  // Top-level items (no parent in the list). A row with a blank code can't be a
  // parent or a child, so it always counts as top-level.
  const wbsTopLevelItems = useMemo(() => wbsItems.filter((item) => {
    const code = (item.code || '').trim();
    if (!code) return true;
    return !wbsItems.some((other) => {
      const otherCode = (other.code || '').trim();
      if (!otherCode || otherCode === code) return false;
      return code.startsWith(otherCode + '.');
    });
  }), [wbsItems]);

  const wbsTotalWeight = useMemo(() => wbsTopLevelItems.reduce((sum, item) => {
    const code = (item.code || '').trim();
    if (isParentItem(code, wbsItems)) {
      return sum + calculateParentTotals(code, wbsItems).weight;
    }
    return sum + parseWBSNum(item.weight);
  }, 0), [wbsTopLevelItems, wbsItems, isParentItem, calculateParentTotals]);

  const wbsOverallProgress = useMemo(() => {
    if (wbsItems.length === 0) return 0;
    const topLevelItems = wbsTopLevelItems;
    const totalWeight = wbsTotalWeight;
    const weightedSum = topLevelItems.reduce((s, i) => {
      const code = (i.code || '').trim();
      let weight: number;
      let progress: number;
      
      if (isParentItem(code, wbsItems)) {
        const totals = calculateParentTotals(code, wbsItems);
        weight = totals.weight;
        progress = totals.progress;
      } else {
        weight = parseWBSNum(i.weight);
        progress = parseWBSNum(i.progress);
      }
      return s + (weight * progress) / 100;
    }, 0);
    
    if (totalWeight > 0) return (weightedSum / totalWeight) * 100;
    return topLevelItems.reduce((s, i) => {
      const code = (i.code || '').trim();
      if (isParentItem(code, wbsItems)) {
        return s + calculateParentTotals(code, wbsItems).progress;
      }
      return s + parseWBSNum(i.progress);
    }, 0) / topLevelItems.length;
  }, [calculateParentTotals, isParentItem, wbsItems, wbsTopLevelItems, wbsTotalWeight]);

  const validationWarnings = useMemo(() => {
    const warnings: string[] = [];
    if (wbsItems.length === 0) warnings.push('No WBS items added');
    if (!pbInput.trim()) warnings.push('PB# not set');
    if (wbsItems.length > 0 && Math.abs(wbsTotalWeight - 100) > 0.01) {
      warnings.push(`Weight total is ${wbsTotalWeight.toFixed(1)}% — should be 100%`);
    }
    return warnings;
  }, [wbsItems, pbInput, wbsTotalWeight]);

  const hasBlockingWarnings = wbsItems.length === 0;

  const syncProjectStatusFromWBS = (items: WBSItem[]) => {
    if (items.length === 0) return;
    const totalWeight = items.reduce((s, i) => s + parseWBSNum(i.weight), 0);
    const weighted = items.reduce((s, i) => s + (parseWBSNum(i.weight) * parseWBSNum(i.progress)) / 100, 0);
    const pct = totalWeight > 0 ? (weighted / totalWeight) * 100 : items.reduce((s, i) => s + parseWBSNum(i.progress), 0) / items.length;
    const rounded = Math.round(pct);
    dataService.updateProject(project.id, { actual_site_progress_percent: rounded }).catch(() => {});
  };

  // Drag-and-drop reordering of WBS rows
  const wbsSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleWbsDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = wbsItems.findIndex((i) => i.id === active.id);
    const newIdx = wbsItems.findIndex((i) => i.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(wbsItems, oldIdx, newIdx);
    setWbsItems(next);
    saveWBS(project.id, next);
  };

  const handleAddWBSItem = () => {
    const newItem: WBSItem = { id: `wbs-${Date.now()}`, code: '', name: '', weight: 0, progress: 0 };
    const next = [...wbsItems, newItem];
    setWbsItems(next);
    saveWBS(project.id, next);
  };

  const handleUpdateWBSItem = (id: string, field: keyof WBSItem, value: string | number) => {
    const next = wbsItems.map((i) => (i.id === id ? { ...i, [field]: value } : i));
    setWbsItems(next);
    saveWBS(project.id, next);
    if (field === 'progress' || field === 'weight') syncProjectStatusFromWBS(next);
  };

  const handleDeleteWBSItem = (id: string) => {
    const next = wbsItems.filter((i) => i.id !== id);
    setWbsItems(next);
    saveWBS(project.id, next);
    syncProjectStatusFromWBS(next);
  };

  const handleSaveProgress = async () => {
    const snapshot: ProgressSnapshot = {
      date: new Date().toISOString(),
      pbNumber: pbInput.trim() || '—',
      wbsItems: JSON.parse(JSON.stringify(wbsItems)),
      overallProgress: wbsOverallProgress,
    };
    try {
      if (editingReportId) {
        // Preserve the original snapshot date when updating in place
        const existing = savedReports.find(r => r.id === editingReportId);
        await updateProgressReport(editingReportId, { ...snapshot, date: existing?.date || snapshot.date });
        setEditingReportId(null);
      } else {
        await saveProgressReport(project.id, snapshot);
      }
      setSaveFeedback(true);
      setTimeout(() => setSaveFeedback(false), 2000);
      await loadSavedReports();
    } catch (e) {
      setExportFeedback({ severity: 'error', message: e instanceof Error ? e.message : 'Failed to save progress report.' });
    }
  };

  const handleLoadSnapshot = (report: StoredProgressReport) => {
    setWbsItems(report.wbsItems);
    setPbInput(report.pbNumber === '—' ? '' : report.pbNumber);
    saveWBS(project.id, report.wbsItems);
    syncProjectStatusFromWBS(report.wbsItems);
    setEditingReportId(report.id);
  };

  const handleDeleteSnapshot = async (report: StoredProgressReport) => {
    if (!window.confirm(`Delete saved progress report "PB${report.pbNumber}"? This cannot be undone.`)) return;
    try {
      await deleteProgressReport(report.id);
      if (editingReportId === report.id) setEditingReportId(null);
      await loadSavedReports();
    } catch (e) {
      setExportFeedback({ severity: 'error', message: e instanceof Error ? e.message : 'Failed to delete progress report.' });
    }
  };

  const handleApplyTemplate = (templateKey: string) => {
    const template = WBS_TEMPLATES[templateKey];
    if (!template) return;
    if (wbsItems.length > 0) {
      if (!window.confirm('Replace current WBS with template? This cannot be undone.')) return;
    }
    const newItems: WBSItem[] = template.items.map((t, i) => ({
      id: `wbs-${Date.now()}-${i}`,
      code: t.code,
      name: t.name,
      weight: 0,
      progress: 0,
    }));
    setWbsItems(newItems);
    saveWBS(project.id, newItems);
    syncProjectStatusFromWBS(newItems);
  };

  const triggerPhotoInput = () => {
    photoInputRef.current?.click();
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawFiles = Array.from(e.target.files || []);
    e.target.value = '';
    if (rawFiles.length === 0) return;

    // Convert HEIC/HEIF to JPEG before preview or upload
    const files = await Promise.all(rawFiles.map(convertHeicToJpeg));

    // Add to state immediately with local previews
    const newItems: PhotoItem[] = files.map(file => ({
      localId: `photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      uploadStatus: 'uploading' as const,
    }));
    setPhotos(prev => [...prev, ...newItems]);

    if (!oneDriveSignedIn) {
      setPhotos(prev => prev.map(p =>
        newItems.some(n => n.localId === p.localId)
          ? { ...p, uploadStatus: 'error', errorMessage: 'Sign in to OneDrive to upload photos.' }
          : p
      ));
      return;
    }

    try {
      const token = await getOneDriveToken();
      if (!token) throw new Error('Could not get OneDrive token.');
      const driveId = await resolveCorporateDriveId(token);

      // Always resolve the execution folder by project_no so photos land under the
      // IOCT ops folder, not inside a PCS proposal subfolder.
      let execFolderId = resolvedExecFolderId;
      if (!execFolderId) {
        const projectCode = project.project_no || String(project.item_no ?? project.id);
        const execFolder = await ensureExecutionFolder(token, { code: projectCode, name: project.project_name });
        execFolderId = execFolder.id;
        setResolvedExecFolderId(execFolderId);
        if (!localExecutionFolderId) {
          setLocalExecutionFolderId(execFolderId);
          dataService.updateProject(project.id, { executionFolderId: execFolder.id, executionFolderUrl: execFolder.webUrl }).catch(() => {});
        }
      }

      // Ensure Photos subfolder
      let folderId = photosFolderId;
      if (!folderId) {
        const photosFolder = await getOrCreateChildFolderById(token, driveId, execFolderId, 'Photos');
        folderId = photosFolder.id;
        setPhotosFolderId(folderId);
        if (photosFolder.webUrl) setPhotosFolderUrl(photosFolder.webUrl);
      }

      const reportPrefix = (`${project.project_no || String(project.item_no ?? project.id)}-PB${pbInput.trim() || '01'}`)
        .replace(/[<>:"/\\|?*\s]/g, '_');

      for (const item of newItems) {
        try {
          const ext = (item.file!.name.split('.').pop() || 'jpg').toLowerCase();
          const filename = `${reportPrefix}_${Date.now()}.${ext}`;
          const [uploaded, thumbnailDataUrl] = await Promise.all([
            uploadFileToFolderById(token, driveId, folderId, filename, item.file!),
            generateThumbnail(item.file!),
          ]);
          setPhotos(prev => prev.map(p => p.localId === item.localId ? {
            ...p,
            oneDriveId: uploaded.id,
            filename: uploaded.name,
            webUrl: uploaded.webUrl,
            uploadedAt: new Date().toISOString(),
            thumbnailDataUrl: thumbnailDataUrl || undefined,
            uploadStatus: 'done' as const,
          } : p));
        } catch (err) {
          setPhotos(prev => prev.map(p => p.localId === item.localId ? {
            ...p,
            uploadStatus: 'error' as const,
            errorMessage: err instanceof Error ? err.message : 'Upload failed',
          } : p));
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setPhotos(prev => prev.map(p =>
        newItems.some(n => n.localId === p.localId) && p.uploadStatus === 'uploading'
          ? { ...p, uploadStatus: 'error' as const, errorMessage: msg }
          : p
      ));
    }
  };

  const handleRemovePhoto = (localId: string) => {
    setPhotos(prev => {
      const removed = prev.find(p => p.localId === localId);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter(p => p.localId !== localId);
    });
  };

  const handleCaptionChange = (localId: string, caption: string) => {
    setPhotos(prev => prev.map(p => p.localId === localId ? { ...p, caption } : p));
  };

  const handlePhotoDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setPhotos(prev => {
      const oldIdx = prev.findIndex(p => p.localId === active.id);
      const newIdx = prev.findIndex(p => p.localId === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const buildPdf = async (preview: boolean): Promise<Blob | { blob: Blob; filename: string; missingPhotos?: number } | void> => {
    const issuer = REPORT_ISSUERS[reportCompany];

    // Pre-fetch embeddable photos. A photo is embeddable when its original file
    // is still in memory (even if the OneDrive upload failed) or when it can be
    // re-fetched from OneDrive — don't silently drop attached photos.
    const exportablePhotos = photos.filter(p => p.file || (p.uploadStatus === 'done' && p.oneDriveId));
    const photoDataUrls = new Map<string, string>();
    if (exportablePhotos.length > 0) {
      let pdToken: string | null = null;
      let pdDriveId: string | null = null;
      if (oneDriveSignedIn) {
        try {
          pdToken = await getOneDriveToken();
          if (pdToken) pdDriveId = await resolveCorporateDriveId(pdToken);
        } catch (_) {}
      }
      await Promise.allSettled(exportablePhotos.map(async (photo) => {
        try {
          let blob: Blob | undefined;
          if (photo.file) {
            blob = photo.file;
          } else if (pdToken && pdDriveId && photo.oneDriveId) {
            blob = await fetchDriveItemBlob(pdToken, pdDriveId, photo.oneDriveId);
            const lowerName = (photo.filename || '').toLowerCase();
            if (blob && (lowerName.endsWith('.heic') || lowerName.endsWith('.heif') ||
                blob.type === 'image/heic' || blob.type === 'image/heif')) {
              blob = await convertHeicToJpeg(new File([blob], photo.filename || 'photo.heic', { type: blob.type }));
            }
          }
          if (blob) {
            const dataUrl = await normalizeImageOrientation(blob);
            photoDataUrls.set(photo.localId, dataUrl);
          }
        } catch (_) {}
      }));
    }
    const missingPhotos = exportablePhotos.length - photoDataUrls.size;

    // Pixel size of each embeddable photo so the PDF can fit it without distortion.
    const pdfPhotos: ProgressReportPhoto[] = [];
    for (const photo of exportablePhotos) {
      const dataUrl = photoDataUrls.get(photo.localId);
      if (!dataUrl) continue;
      const size = await new Promise<{ width: number; height: number }>((res) => {
        const img = new Image();
        img.onload = () => res({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => res({ width: 0, height: 0 });
        img.src = dataUrl;
      });
      const userCaption = (photo.caption || '').trim();
      const rawName = photo.filename || photo.file?.name || '';
      const caption = userCaption || rawName.replace(/^[^_]*_[^_]*_\d+\./, '').slice(0, 40) || rawName;
      pdfPhotos.push({ dataUrl, ...size, caption });
    }

    const rows: ProgressReportRow[] = wbsItems.map((i) => {
      const code = (i.code || '').trim();
      const isParent = isParentItem(code, wbsItems);
      const totals = isParent
        ? calculateParentTotals(code, wbsItems)
        : { weight: parseWBSNum(i.weight), progress: parseWBSNum(i.progress) };
      return { code: code || '—', name: i.name || '—', ...totals, isParent, level: getIndentLevel(code) };
    });

    // Approvers: the signatory cards, else the project's legacy client_approver text.
    let pdfApprovers = approvers.slice(0, 3);
    if (pdfApprovers.length === 0) {
      const { name, designation } = parseClientApprover(project.client_approver);
      pdfApprovers = [{ name, designation, company: (project.account_name || '').trim() }];
    }

    const projectNo = project.project_no || String(project.item_no ?? project.id) || '—';
    const pbNumRaw = pbInput.trim() || '0';
    const pbNum = pbNumRaw === '—' || pbNumRaw === '' ? '01' : String(Number(pbNumRaw) || 1).padStart(2, '0');

    const { doc, filename } = buildProgressReportPdf({
      reportCompany,
      projectName: project.project_name || '—',
      projectNo,
      poNumber: project.po_number || '—',
      client: project.account_name || '—',
      location: project.project_location || '—',
      reportDate: preparedBy.date,
      pbNumber: pbNum,
      rows,
      totalWeight: wbsTotalWeight,
      overallProgress: wbsOverallProgress,
      preparedBy: {
        name: (preparedBy.name || currentUser?.full_name || currentUser?.username || currentUser?.email || '').trim(),
        designation: (preparedBy.designation || '').trim() || undefined,
      },
      approvers: pdfApprovers,
      photos: pdfPhotos,
    }, { logoDataUrl: await loadReportLogo(issuer), bodyFontBase64: arialNarrowBase64 });

    // Work Schedule attachment: the Gantt generator's own PDF (its saved export settings), appended after the report.
    let blob = doc.output('blob') as Blob;
    if (hasSchedule && (scheduleAttach.gantt || scheduleAttach.sCurve)) {
      const attachment = await renderScheduleAttachment(String(project.id), { code: projectNo, name: project.project_name || '' }, scheduleAttach)
        .catch((e) => { console.warn('[ProgressReport] Work Schedule attachment skipped:', e); return null; });
      if (attachment) blob = new Blob([await appendPdfs(doc.output('arraybuffer'), [attachment])], { type: 'application/pdf' });
    }

    if (preview) return blob;
    saveAs(blob, filename);
    return { blob, filename, missingPhotos };
  };

  const handlePreview = async () => {
    const result = await buildPdf(true);
    if (result instanceof Blob) onPreview(result, 'Progress Report');
  };

  const handleExport = async () => {
    setExporting(true);
    setExportFeedback(null);
    try {
      const result = await buildPdf(false);
      // result is { blob, filename, missingPhotos } for preview=false
      if (!result || result instanceof Blob) return;
      const { blob, filename, missingPhotos = 0 } = result;
      const photoNote = missingPhotos > 0
        ? ` ${missingPhotos} attached photo(s) could not be included in the PDF — sign in to OneDrive so saved photos can be fetched.`
        : '';

      // Show billing hint if a PB# is set
      if (pbInput.trim()) setShowBillingHint(true);

      // Best-effort OneDrive upload — auto-creates the execution folder when it doesn't exist yet
      if (!isCorporateOneDriveConfigured()) {
        setExportFeedback({ severity: 'warning', message: 'PDF exported locally. OneDrive is not configured.' + photoNote });
        return;
      }
      if (!oneDriveSignedIn) {
        setExportFeedback({ severity: 'warning', message: 'PDF exported locally. Sign in to OneDrive to upload it.' + photoNote });
        return;
      }
      const token = await getOneDriveToken();
      if (!token) {
        setExportFeedback({ severity: 'warning', message: 'PDF exported locally. Could not get OneDrive access token.' + photoNote });
        return;
      }
      const driveId = await resolveCorporateDriveId(token);
      // Always resolve by project_no so PDFs land in the IOCT ops folder,
      // not inside a PCS proposal subfolder pointed to by a stale executionFolderId.
      let folderId = resolvedExecFolderId;
      if (!folderId) {
        const projectCode = project.project_no || String(project.item_no ?? project.id);
        const folder = await ensureExecutionFolder(token, { code: projectCode, name: project.project_name });
        folderId = folder.id;
        setResolvedExecFolderId(folderId);
        if (!localExecutionFolderId) {
          setLocalExecutionFolderId(folderId);
          dataService.updateProject(project.id, { executionFolderId: folder.id, executionFolderUrl: folder.webUrl }).catch(() => {});
        }
      }
      await uploadFileToFolderById(token, driveId, folderId, filename, blob);
      setExportFeedback({ severity: photoNote ? 'warning' : 'success', message: `PDF exported and uploaded to OneDrive: ${filename}.` + photoNote });
    } catch (e) {
      setExportFeedback({ severity: 'error', message: e instanceof Error ? e.message : 'PDF exported locally, but OneDrive upload failed.' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Scrollable accordion area */}
      <Box sx={{ flex: 1, overflow: 'auto', pb: 10 }}>

        {/* Page-level feedback */}
        {exportFeedback && (
          <Alert severity={exportFeedback.severity} onClose={() => setExportFeedback(null)} sx={{ mb: 1 }}>
            {exportFeedback.message}
          </Alert>
        )}
        {showBillingHint && (
          <Alert
            severity="info"
            onClose={() => setShowBillingHint(false)}
            sx={{ mb: 1 }}
            action={
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  sessionStorage.setItem('selectedProjectId', String(project.id));
                  navigate('/dashboard');
                }}
              >
                Go to Billing
              </Button>
            }
          >
            Progress report exported for <strong>{pbInput}</strong>. Ready to create the invoice?
          </Alert>
        )}

        {/* Accordion 1: Report Setup */}
        <Accordion
          expanded={expandedSections.setup}
          onChange={() => toggleSection('setup')}
          disableGutters
          elevation={0}
          sx={sectionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Typography variant="h6" sx={sectionTitleSx}>
              Report Setup
            </Typography>
            {pbInput && preparedBy.date && (
              <Chip label="Ready" size="small" color="success" variant="outlined" sx={{ mr: 1 }} />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="PB #" placeholder="e.g. 1" value={pbInput} onChange={(e) => setPbInput(e.target.value)} sx={{ width: 80 }} />
              <TextField size="small" label="Date" type="date" value={preparedBy.date} onChange={(e) => setPreparedBy((p) => ({ ...p, date: e.target.value }))} sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}>Work Schedule attachment</Typography>
              <FormControlLabel
                control={<Switch size="small" checked={hasSchedule && scheduleAttach.gantt} onChange={() => toggleScheduleAttach('gantt')} disabled={!hasSchedule} />}
                label={<Typography variant="body2">Attach Gantt chart</Typography>}
              />
              <FormControlLabel
                control={<Switch size="small" checked={hasSchedule && scheduleAttach.sCurve} onChange={() => toggleScheduleAttach('sCurve')} disabled={!hasSchedule} />}
                label={<Typography variant="body2">Attach S-Curve</Typography>}
              />
              <Typography variant="caption" color="text.secondary" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                {scheduleTaskCount === null ? 'Checking the Work Schedule…'
                  : hasSchedule ? `Attached after the report, exactly as the Work Schedule's Export Gantt makes it (${scheduleTaskCount} tasks).`
                  : <>No Work Schedule yet. <Button size="small" sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline', textTransform: 'none' }} onClick={() => navigate(`/projects/${project.id}/schedule`)}>Build one</Button> to attach it.</>}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 2: Work Breakdown Structure */}
        <Accordion
          expanded={expandedSections.wbs}
          onChange={() => toggleSection('wbs')}
          disableGutters
          elevation={0}
          sx={sectionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Typography variant="h6" sx={sectionTitleSx}>
              Work Breakdown Structure
            </Typography>
            {wbsItems.length > 0 && (
              <Chip label={`${wbsItems.length} items`} size="small" variant="outlined" sx={{ mr: 1 }} />
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, pt: 1.5, pb: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 240 } }}>
                <InputLabel id="wbs-template-label" shrink>Template</InputLabel>
                <Select
                  labelId="wbs-template-label"
                  notched
                  value=""
                  label="Template"
                  onChange={(e) => {
                    if (e.target.value) handleApplyTemplate(e.target.value as string);
                  }}
                  displayEmpty
                >
                  <MenuItem value="" disabled><em>Load a template...</em></MenuItem>
                  {Object.entries(WBS_TEMPLATES).map(([key, tpl]) => (
                    <MenuItem key={key} value={key}>{tpl.label} ({tpl.items.length} items)</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddWBSItem}
                sx={{ backgroundColor: NET_PACIFIC_COLORS.primary, '&:hover': { backgroundColor: NET_PACIFIC_COLORS.secondary } }}
              >
                Add WBS item
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, pb: 1.5 }}>
              Tip: a row becomes a bold <strong>sub-header</strong> automatically when other rows use its code as a prefix — e.g. code <strong>1</strong> is the &quot;Engineering Services&quot; header, and <strong>1.1</strong> / <strong>1.2</strong> are its items (their weight &amp; progress roll up into the header). Drag the <DragIndicatorIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> handle to reorder rows.
            </Typography>
            <DndContext sensors={wbsSensors} collisionDetection={closestCenter} onDragEnd={handleWbsDragEnd}>
            <TableContainer sx={{ border: 0, borderRadius: 0, overflow: 'auto' }}>
              <Table stickyHeader size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...wbsHeadSx, width: 40, px: 0.5 }} />
                    <TableCell sx={{ ...wbsHeadSx, width: 100 }}>Code</TableCell>
                    <TableCell sx={{ ...wbsHeadSx, minWidth: 200 }}>Name</TableCell>
                    <TableCell align="right" sx={{ ...wbsHeadSx, width: 100 }}>Weight %</TableCell>
                    <TableCell align="right" sx={{ ...wbsHeadSx, width: 100 }}>Progress %</TableCell>
                    <TableCell align="center" sx={{ ...wbsHeadSx, width: 72 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wbsItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5, px: 2, color: 'text.secondary', fontSize: '0.9375rem' }}>No WBS items. Click &quot;Add WBS item&quot; below.</TableCell>
                    </TableRow>
                  ) : (
                    <SortableContext items={wbsItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                    {wbsItems.map((item, index) => {
                      const code = (item.code || '').trim();
                      const isParent = isParentItem(code, wbsItems);
                      const indentLevel = getIndentLevel(code);
                      const indentPx = indentLevel * 24; // 24px per level
                      const parentTotals = isParent ? calculateParentTotals(code, wbsItems) : null;

                      return (
                        <SortableItem key={item.id} id={item.id}>
                          {({ setNodeRef, style, attributes, listeners }) => (
                        <TableRow
                          ref={setNodeRef}
                          style={style}
                          hover
                          sx={{
                            // Sub-headers get a light brand tint; items are lightly striped.
                            bgcolor: isParent ? 'rgba(44,90,160,0.07)' : (index % 2 === 0 ? '#fff' : 'rgba(0,0,0,0.02)'),
                            '&:hover': {
                              bgcolor: isParent ? 'rgba(44,90,160,0.11)' : undefined,
                            }
                          }}
                        >
                          <TableCell
                            {...attributes}
                            {...listeners}
                            sx={{ width: 40, px: 0.5, textAlign: 'center', verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9', cursor: 'grab', color: 'text.disabled', touchAction: 'none', '&:active': { cursor: 'grabbing' } }}
                            title="Drag to reorder"
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </TableCell>
                          <TableCell sx={{ py: 0.75, px: 1.5, verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' }}>
                            <TextField
                              size="small"
                              fullWidth
                              value={item.code}
                              onChange={(e) => handleUpdateWBSItem(item.id, 'code', e.target.value)}
                              placeholder="1.1"
                              variant="outlined"
                              sx={{
                                ...wbsInputSx,
                                '& .MuiInputBase-input': {
                                  ...wbsInputSx['& .MuiInputBase-input'],
                                  fontWeight: isParent ? 700 : 'normal',
                                  fontFamily: isParent ? '"Arial Narrow", Arial, sans-serif' : undefined,
                                }
                              }}
                              inputProps={{ maxLength: 20 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.75, px: 1.5, verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' }}>
                            <Box sx={{ pl: `${indentPx}px` }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={item.name}
                                onChange={(e) => handleUpdateWBSItem(item.id, 'name', e.target.value)}
                                placeholder="Work package name"
                                variant="outlined"
                                sx={{
                                  ...wbsInputSx,
                                  '& .MuiInputBase-input': {
                                    ...wbsInputSx['& .MuiInputBase-input'],
                                    fontWeight: isParent ? 700 : 'normal',
                                    fontFamily: isParent ? '"Arial Narrow", Arial, sans-serif' : undefined,
                                  }
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.75, px: 1.5, verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' }}>
                            {isParent ? (
                              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: '#1e293b', fontFamily: '"Arial Narrow", Arial, sans-serif' }}>
                                {parentTotals ? parentTotals.weight.toFixed(2) : '0.00'}
                              </Typography>
                            ) : (
                              <TextField
                                size="small"
                                type="number"
                                fullWidth
                                value={wbsDraft[item.id]?.weight ?? String(parseWBSNum(item.weight))}
                                onFocus={(e) => (e.target as HTMLInputElement).select()}
                                onChange={(e) => setWbsDraft((prev) => ({ ...prev, [item.id]: { ...prev[item.id], weight: e.target.value } }))}
                                onBlur={() => { const raw = wbsDraft[item.id]?.weight; if (raw !== undefined) { handleUpdateWBSItem(item.id, 'weight', parseWBSNum(raw)); setWbsDraft((prev) => { const next = { ...prev }; if (next[item.id]) { delete next[item.id].weight; if (Object.keys(next[item.id]).length === 0) delete next[item.id]; } return next; }); } }}
                                inputProps={{ min: 0, max: 100, step: 1 }}
                                variant="outlined"
                                sx={wbsNumInputSx}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.75, px: 1.5, verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' }}>
                            {isParent ? (
                              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: '#1e293b', fontFamily: '"Arial Narrow", Arial, sans-serif' }}>
                                {parentTotals ? parentTotals.progress.toFixed(2) : '0.00'}
                              </Typography>
                            ) : (
                              <TextField
                                size="small"
                                type="number"
                                fullWidth
                                value={wbsDraft[item.id]?.progress ?? String(parseWBSNum(item.progress))}
                                onFocus={(e) => (e.target as HTMLInputElement).select()}
                                onChange={(e) => setWbsDraft((prev) => ({ ...prev, [item.id]: { ...prev[item.id], progress: e.target.value } }))}
                                onBlur={() => { const raw = wbsDraft[item.id]?.progress; if (raw !== undefined) { handleUpdateWBSItem(item.id, 'progress', parseWBSNum(raw)); setWbsDraft((prev) => { const next = { ...prev }; if (next[item.id]) { delete next[item.id].progress; if (Object.keys(next[item.id]).length === 0) delete next[item.id]; } return next; }); } }}
                                inputProps={{ min: 0, max: 100, step: 1 }}
                                variant="outlined"
                                sx={wbsNumInputSx}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.75, px: 1.5, verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' }}>
                            <IconButton size="small" onClick={() => handleDeleteWBSItem(item.id)} title="Delete" color="error"><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                          )}
                        </SortableItem>
                      );
                    })}
                    </SortableContext>
                  )}
                </TableBody>
                {wbsItems.length > 0 && (() => {
                  const totalWeight = wbsTotalWeight;

                  return (
                    <TableFooter>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell colSpan={3} sx={wbsTotalSx}>Total</TableCell>
                        <TableCell align="right" sx={{ ...wbsTotalSx, color: Math.abs(totalWeight - 100) > 0.01 ? 'error.main' : undefined }}>{totalWeight.toFixed(2)}%</TableCell>
                        <TableCell align="right" sx={wbsTotalSx}>{wbsOverallProgress.toFixed(2)}%</TableCell>
                        <TableCell sx={wbsTotalSx} />
                      </TableRow>
                    </TableFooter>
                  );
                })()}
              </Table>
            </TableContainer>
            </DndContext>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 3: Signatories */}
        <Accordion
          expanded={expandedSections.signatories}
          onChange={() => toggleSection('signatories')}
          disableGutters
          elevation={0}
          sx={sectionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Typography variant="h6" sx={sectionTitleSx}>
              Signatories
            </Typography>
            {(preparedBy.name || approvers.length > 0) && (
              <Chip label="Set" size="small" color="success" variant="outlined" sx={{ mr: 1 }} />
            )}
          </AccordionSummary>
          <AccordionDetails>
            {/* Prepared by card */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
              Prepared by
            </Typography>
            {!editingPreparedBy ? (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{preparedBy.name || '(not set)'}</Typography>
                  {(preparedBy.designation || preparedBy.company) && (
                    <Typography variant="caption" color="text.secondary">
                      {[preparedBy.designation, preparedBy.company].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
                <Button size="small" onClick={() => setEditingPreparedBy(true)} sx={{ textTransform: 'none', color: NET_PACIFIC_COLORS.primary }}>
                  Edit
                </Button>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <TextField size="small" label="Name" value={preparedBy.name} onChange={(e) => setPreparedBy(p => ({ ...p, name: e.target.value }))} sx={{ flex: 1, minWidth: 150 }} />
                  <TextField size="small" label="Designation" value={preparedBy.designation} onChange={(e) => setPreparedBy(p => ({ ...p, designation: e.target.value }))} sx={{ flex: 1, minWidth: 120 }} />
                  <TextField size="small" label="Company" value={preparedBy.company} onChange={(e) => setPreparedBy(p => ({ ...p, company: e.target.value }))} sx={{ flex: 1, minWidth: 120 }} />
                </Box>
                <Button size="small" onClick={() => setEditingPreparedBy(false)} sx={{ textTransform: 'none' }}>Done</Button>
              </Paper>
            )}

            {/* Approved by cards */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
              Approved by
            </Typography>
            {approvers.map((approver, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                {editingApproverIndex === index ? (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <TextField size="small" label="Name" value={approver.name} onChange={(e) => { const updated = [...approvers]; updated[index] = { ...updated[index], name: e.target.value }; setApprovers(updated); }} sx={{ flex: 1, minWidth: 150 }} />
                      <TextField size="small" label="Designation" value={approver.designation} onChange={(e) => { const updated = [...approvers]; updated[index] = { ...updated[index], designation: e.target.value }; setApprovers(updated); }} sx={{ flex: 1, minWidth: 120 }} />
                      <TextField size="small" label="Company" value={approver.company} onChange={(e) => { const updated = [...approvers]; updated[index] = { ...updated[index], company: e.target.value }; setApprovers(updated); }} sx={{ flex: 1, minWidth: 120 }} />
                    </Box>
                    <Button size="small" onClick={() => setEditingApproverIndex(null)} sx={{ textTransform: 'none' }}>Done</Button>
                  </Box>
                ) : (
                  <>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{approver.name || '(not set)'}</Typography>
                      {(approver.designation || approver.company) && (
                        <Typography variant="caption" color="text.secondary">
                          {[approver.designation, approver.company].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Button size="small" onClick={() => setEditingApproverIndex(index)} sx={{ textTransform: 'none', color: NET_PACIFIC_COLORS.primary, minWidth: 0 }}>
                        Edit
                      </Button>
                      <IconButton size="small" onClick={() => setApprovers(prev => prev.filter((_, i) => i !== index))} color="error" title="Remove">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                )}
              </Paper>
            ))}
            {approvers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                No approvers added yet.
              </Typography>
            )}
            {approvers.length < 3 && (
              <Autocomplete
                freeSolo
                size="small"
                options={clientContacts}
                getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
                isOptionEqualToValue={(opt, val) => opt.name === (typeof val === 'string' ? val : val.name)}
                inputValue={addApproverInput}
                onInputChange={(_, newVal) => setAddApproverInput(newVal)}
                onChange={(_, newVal) => {
                  if (newVal && typeof newVal !== 'string') {
                    setApprovers(prev => [...prev, { name: newVal.name, designation: newVal.designation, company: newVal.company }]);
                    setAddApproverInput('');
                  } else if (typeof newVal === 'string' && newVal.trim()) {
                    setApprovers(prev => [...prev, { name: newVal.trim(), designation: '', company: '' }]);
                    setAddApproverInput('');
                  }
                }}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.name + opt.designation}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{opt.name}</Typography>
                      {(opt.designation || opt.company) && (
                        <Typography variant="caption" color="text.secondary">
                          {[opt.designation, opt.company].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Add approver" size="small" placeholder={clientContacts.length > 0 ? 'Search client contacts...' : 'Type a name'} />
                )}
                sx={{ maxWidth: 400 }}
                noOptionsText="No client contacts found"
              />
            )}
          </AccordionDetails>
        </Accordion>

        {/* Accordion 4: Site Photos */}
        <Accordion
          expanded={expandedSections.photos}
          onChange={() => toggleSection('photos')}
          disableGutters
          elevation={0}
          sx={sectionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Typography variant="h6" sx={sectionTitleSx}>
              Site Photos
            </Typography>
            {photos.length > 0 && (
              <Chip
                label={`${photos.length} photo${photos.length !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              {!oneDriveConfigured ? (
                <Typography variant="caption" color="text.secondary">OneDrive is not configured for this environment.</Typography>
              ) : !oneDriveSignedIn ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={oneDriveAuthLoading ? <CircularProgress size={14} /> : <CameraAltIcon />}
                  disabled={oneDriveAuthLoading}
                  onClick={() => oneDriveLogin()}
                  sx={{ textTransform: 'none', borderColor: NET_PACIFIC_COLORS.primary, color: NET_PACIFIC_COLORS.primary }}
                >
                  Sign in to OneDrive to attach photos
                </Button>
              ) : (
                <Button
                  size="small"
                  startIcon={<CameraAltIcon />}
                  onClick={triggerPhotoInput}
                  sx={{ textTransform: 'none', color: NET_PACIFIC_COLORS.primary, borderColor: NET_PACIFIC_COLORS.primary }}
                  variant="outlined"
                >
                  Add photos
                </Button>
              )}
              {photosFolderUrl && (
                <Button
                  size="small"
                  variant="text"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  component="a"
                  href={photosFolderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem' }}
                >
                  Open Photos folder
                </Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Photos are uploaded to the project&apos;s OneDrive <strong>Photos</strong> folder and embedded as a grid at the end of the exported PDF.
            </Typography>
            {photos.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Add a caption under each photo — it prints beneath the image in the PDF (the filename is used if left blank). Drag the <DragIndicatorIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> handle on a photo to change the order they appear.
                </Typography>
                <DndContext sensors={wbsSensors} collisionDetection={closestCenter} onDragEnd={handlePhotoDragEnd}>
                <SortableContext items={photos.map(p => p.localId)} strategy={rectSortingStrategy}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {photos.map(p => (
                    <SortableItem key={p.localId} id={p.localId}>
                      {({ setNodeRef, style, attributes, listeners }) => (
                        <Box ref={setNodeRef} style={style} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: 110 }}>
                          <Tooltip title={p.uploadStatus === 'error' ? (p.errorMessage || 'Upload failed') : (p.filename || p.file?.name || '')}>
                            <Box sx={{ position: 'relative', width: 110, height: 84, borderRadius: 1, overflow: 'hidden', border: '1px solid #e2e8f0', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {(p.previewUrl || p.thumbnailDataUrl) && (
                                <img src={p.previewUrl ?? p.thumbnailDataUrl} alt={p.filename || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              )}
                              {p.uploadStatus === 'uploading' && (
                                <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <CircularProgress size={20} />
                                </Box>
                              )}
                              <Box {...attributes} {...listeners} title="Drag to reorder"
                                sx={{ position: 'absolute', top: 2, left: 2, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', borderRadius: 0.5, p: 0.25, display: 'flex', cursor: 'grab', touchAction: 'none', '&:active': { cursor: 'grabbing' }, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                                <DragIndicatorIcon sx={{ fontSize: 14 }} />
                              </Box>
                              {p.uploadStatus === 'done' && p.webUrl && (
                                <IconButton size="small" component="a" href={p.webUrl} target="_blank" rel="noopener noreferrer"
                                  sx={{ position: 'absolute', bottom: 2, right: 20, bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', p: 0.25, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                                  <OpenInNewIcon sx={{ fontSize: 11 }} />
                                </IconButton>
                              )}
                              {p.uploadStatus === 'error' && (
                                <Chip label="Error" size="small" color="error" sx={{ position: 'absolute', bottom: 2, left: 2, fontSize: '0.6rem', height: 16 }} />
                              )}
                              <IconButton size="small" onClick={() => handleRemovePhoto(p.localId)}
                                sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', p: 0.25, '&:hover': { bgcolor: 'rgba(200,0,0,0.8)' } }}>
                                <DeleteIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Box>
                          </Tooltip>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Caption"
                            value={p.caption ?? ''}
                            onChange={(e) => handleCaptionChange(p.localId, e.target.value)}
                            inputProps={{ maxLength: 80 }}
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.5, px: 0.75 } }}
                          />
                        </Box>
                      )}
                    </SortableItem>
                  ))}
                </Box>
                </SortableContext>
                </DndContext>
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Accordion 5: Saved Reports */}
        <Accordion
          expanded={expandedSections.savedReports}
          onChange={() => toggleSection('savedReports')}
          disableGutters
          elevation={0}
          sx={sectionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Typography variant="h6" sx={sectionTitleSx}>
              Saved Reports
            </Typography>
            {savedReports.length > 0 && (
              <Chip label={String(savedReports.length)} size="small" variant="outlined" sx={{ mr: 1 }} />
            )}
            {savedLoading && <CircularProgress size={16} sx={{ color: NET_PACIFIC_COLORS.primary, mr: 1 }} />}
          </AccordionSummary>
          <AccordionDetails>
            {savedReports.length > 0 ? (
              <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1, maxHeight: 280 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={savedHeadSx}>PB#</TableCell>
                      <TableCell sx={savedHeadSx}>Date</TableCell>
                      <TableCell align="right" sx={savedHeadSx}>Progress</TableCell>
                      <TableCell align="right" sx={savedHeadSx}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {savedReports.map((s) => (
                      <TableRow key={s.id} hover selected={editingReportId === s.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' }, '& td': { fontSize: '0.8rem' } }}>
                        <TableCell>PB{s.pbNumber}</TableCell>
                        <TableCell>{new Date(s.date).toLocaleDateString('en-US', { dateStyle: 'medium' })}</TableCell>
                        <TableCell align="right">{Math.round(s.overallProgress * 100) / 100}%</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => handleLoadSnapshot(s)} sx={{ color: NET_PACIFIC_COLORS.primary }}>Load</Button>
                          <IconButton size="small" color="error" onClick={() => handleDeleteSnapshot(s)} title="Delete snapshot" aria-label="Delete snapshot">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No saved reports yet. Use the Save button to snapshot the current progress.
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>


      </Box>

      {/* Hidden file input for site photos */}
      <input ref={photoInputRef} type="file" accept="image/*,.heic,.heif" multiple style={{ display: 'none' }} onChange={handlePhotoSelect} />

      {/* Sticky bottom bar */}
      <Paper
        elevation={3}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          borderTop: `2px solid ${NET_PACIFIC_COLORS.primary}`,
          pl: 2,
          pr: 9,
          py: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Left: progress bar */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: NET_PACIFIC_COLORS.primary }}>
                Overall: {wbsOverallProgress.toFixed(1)}%
              </Typography>
              {editingReportId && savedReports.find(r => r.id === editingReportId) && (
                <Chip
                  label={`Editing PB${savedReports.find(r => r.id === editingReportId)!.pbNumber}`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, wbsOverallProgress)}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  bgcolor: wbsOverallProgress >= 100 ? 'success.main' : NET_PACIFIC_COLORS.primary,
                },
              }}
            />
          </Box>
          {/* Right: action buttons */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexShrink: { xs: 1, sm: 0 } }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSaveProgress}
              disabled={wbsItems.length === 0}
              sx={{ borderColor: NET_PACIFIC_COLORS.primary, color: NET_PACIFIC_COLORS.primary }}
            >
              {saveFeedback ? 'Saved' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={handlePreview}
              disabled={hasBlockingWarnings}
              sx={{ borderColor: NET_PACIFIC_COLORS.primary, color: NET_PACIFIC_COLORS.primary }}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExport}
              disabled={hasBlockingWarnings || exporting}
              sx={{ backgroundColor: NET_PACIFIC_COLORS.primary, '&:hover': { backgroundColor: NET_PACIFIC_COLORS.secondary } }}
            >
              {exporting ? 'Uploading...' : 'Export PDF'}
            </Button>
          </Box>
        </Box>
        {/* Validation warnings */}
        {validationWarnings.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {validationWarnings.map((w) => (
              <Alert key={w} severity="warning" sx={{ py: 0, px: 1, mb: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                {w}
              </Alert>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ProgressReportTab;
